/**
 * Onboarding Pipeline screen — admin view of the publisher acquisition
 * funnel (long-tail track, paired with the 20-affiliate track that
 * lives on the dashboard).
 *
 * Three tabs: Overview, Prospects, and Outreach — the agentic controls
 * (AgenticConfig kill switch, Apollo/ICP targeting, and run-now triggers).
 * All ported here; the legacy app.nurev.io pipeline view is retired.
 *
 * The legacy mistakenly mixed "Creators" labels into a few KPI cards
 * (calling Active Publishers "Active Creators" while still reading
 * the `onboardingStates.active` field). Fixed to consistent
 * "Publishers" language in the port.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Pause, Play } from 'lucide-react';
import {
  AgenticIcp,
  CreateProspectInput,
  ONBOARDING_STATES,
  OnboardingStateName,
  Prospect,
  PROSPECT_STATUSES,
  ProspectSource,
  ProspectStatus,
  TrafficTier
} from '@/api/pipeline';
import {
  useAgenticConfig,
  useCreateProspect,
  usePauseProspect,
  usePipelineFunnel,
  usePipelineOverview,
  usePipelineProspects,
  useProspectDetail,
  useResumeProspect,
  useRunProspecting,
  useRunSend,
  useUpdateAgenticConfig
} from '@/hooks/usePipeline';
import {
  ActionButton,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime, fmtNum } from '@/lib/format';

const STATE_LABEL: Record<OnboardingStateName, string> = {
  interested: 'Interested',
  email_sent: 'Email Sent',
  link_clicked: 'Link Clicked',
  form_started: 'Form Started',
  form_complete: 'Form Complete',
  account_created: 'Account Created',
  keys_issued: 'Keys Issued',
  active: 'Active'
};

const STATE_BADGE: Record<OnboardingStateName, string> = {
  interested: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  email_sent: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  link_clicked: 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-500/30',
  form_started: 'bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/40',
  form_complete: 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30',
  account_created: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30',
  keys_issued: 'bg-pink-500/20 text-pink-300 ring-1 ring-pink-500/30',
  active: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
};

const STATUS_BADGE: Record<ProspectStatus, string> = {
  new: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
  contacted: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  interested: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  registered: 'bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/40',
  active: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  dormant: 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30',
  churned: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
};

const StateBadge = ({ state }: { state?: OnboardingStateName }) =>
  state ? (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${STATE_BADGE[state]}`}
    >
      {STATE_LABEL[state]}
    </span>
  ) : (
    <span className="text-xs text-nurev-muted">—</span>
  );

const StatusBadge = ({ status }: { status?: ProspectStatus }) =>
  status ? (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[status]}`}
    >
      {status}
    </span>
  ) : (
    <span className="text-xs text-nurev-muted">—</span>
  );

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data: overview, isLoading: ovLoading } = usePipelineOverview();
  const { data: funnel, isLoading: fnLoading } = usePipelineFunnel();
  const total = overview?.totalProspects || 0;
  const funnelData = funnel?.funnel || {};
  const currentData = funnel?.current || {};
  const maxFunnel = useMemo(
    () =>
      Object.values(funnelData).reduce((m, v) => Math.max(m, v || 0), 1) || 1,
    [funnelData]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Total Prospects"
          value={fmtNum(overview?.totalProspects)}
          loading={ovLoading}
        />
        <StatCard
          label="Active Publishers"
          value={fmtNum(overview?.onboardingStates?.active)}
          color="text-emerald-300"
          loading={ovLoading}
        />
        <StatCard
          label="Open Escalations"
          value={fmtNum(overview?.openEscalations)}
          color="text-amber-300"
          loading={ovLoading}
        />
        <StatCard
          label="Transitions (24h)"
          value={fmtNum(overview?.transitionsLast24h)}
          color="text-amber-200"
          loading={ovLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>By Onboarding State</SectionLabel>
          {ovLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="space-y-2">
              {ONBOARDING_STATES.map(state => {
                const count = overview?.onboardingStates?.[state] || 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={state} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-right">
                      <StateBadge state={state} />
                    </span>
                    <div className="h-5 flex-1 overflow-hidden rounded bg-white/[0.03]">
                      <div
                        className="h-full rounded bg-amber-500/30 transition-all duration-500"
                        style={{ width: `${Math.max(pct, 1)}%` }}
                      />
                    </div>
                    <span className="w-16 text-right text-xs text-nurev-muted">
                      {count} ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card>
          <SectionLabel>By Prospect Status</SectionLabel>
          {ovLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="space-y-2">
              {PROSPECT_STATUSES.map(status => {
                const count = overview?.prospectStatuses?.[status] || 0;
                return (
                  <div
                    key={status}
                    className="flex items-center justify-between"
                  >
                    <StatusBadge status={status} />
                    <span className="text-sm font-medium text-nurev-text">
                      {fmtNum(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card>
        <SectionLabel>Onboarding Funnel</SectionLabel>
        {fnLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {ONBOARDING_STATES.map(state => {
              const reached = funnelData[state] || 0;
              const current = currentData[state] || 0;
              const pct =
                maxFunnel > 0 ? (reached / maxFunnel) * 100 : 0;
              return (
                <div key={state} className="flex items-center gap-3">
                  <span className="w-28 shrink-0 text-right text-xs text-nurev-muted">
                    {STATE_LABEL[state]}
                  </span>
                  <div className="relative h-7 flex-1 overflow-hidden rounded-lg bg-white/[0.03]">
                    <div
                      className="h-full rounded-lg bg-gradient-to-r from-amber-500/40 to-amber-500/15 transition-all duration-500"
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-between px-3">
                      <span className="text-xs font-medium text-nurev-text">
                        {reached} reached
                      </span>
                      <span className="text-[10px] text-nurev-muted">
                        {current} current
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

    </div>
  );
}

// ─── Prospects tab ────────────────────────────────────────────────────────

interface ProspectsTabProps {
  onSelect: (id: string) => void;
  onAdd: () => void;
}

function ProspectsTab({ onSelect, onAdd }: ProspectsTabProps) {
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState<OnboardingStateName | ''>('');
  const [statusFilter, setStatusFilter] = useState<ProspectStatus | ''>('');
  const [search, setSearch] = useState('');

  const params = useMemo(
    () => ({
      page,
      limit: 20,
      sort: '-createdAt',
      state: stateFilter,
      status: statusFilter,
      q: search.trim()
    }),
    [page, stateFilter, statusFilter, search]
  );
  const { data, isLoading } = usePipelineProspects(params);
  const items = data?.docs || [];
  const totalPages = data?.totalPages || 1;
  const resetPage = () => setPage(1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            resetPage();
          }}
          placeholder="Search domain, company, contact…"
          className="w-64 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
        <Select
          value={stateFilter}
          onChange={v => {
            setStateFilter(v);
            resetPage();
          }}
          options={[
            { value: '', label: 'All States' },
            ...ONBOARDING_STATES.map(s => ({ value: s, label: STATE_LABEL[s] }))
          ]}
        />
        <Select
          value={statusFilter}
          onChange={v => {
            setStatusFilter(v);
            resetPage();
          }}
          options={[
            { value: '', label: 'All Statuses' },
            ...PROSPECT_STATUSES.map(s => ({ value: s, label: s }))
          ]}
        />
        <div className="ml-auto">
          <ActionButton onClick={onAdd} size="sm">
            + Add Prospect
          </ActionButton>
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Domain</th>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Onboarding</th>
                <th className="px-4 py-3 font-medium">Traffic</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Added</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No prospects match these filters.
                  </td>
                </tr>
              ) : (
                items.map(p => (
                  <tr
                    key={p._id}
                    onClick={() => onSelect(p._id)}
                    className="cursor-pointer border-b border-nurev-border/50 transition-colors hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3 font-medium text-nurev-text">
                      {p.domain}
                      {p.sequencePaused && (
                        <span
                          className="ml-2 inline-block rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-300 ring-1 ring-amber-500/30"
                          title={p.sequencePausedReason || 'Sequence paused'}
                        >
                          PAUSED
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {p.companyName || '—'}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {p.contactName || p.contactEmail || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3">
                      <StateBadge state={p.onboardingState?.state} />
                    </td>
                    <td className="px-4 py-3 capitalize text-nurev-muted">
                      {p.monthlyTrafficTier || '—'}
                    </td>
                    <td className="px-4 py-3 capitalize text-nurev-muted">
                      {p.source || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-nurev-muted">
                      {fmtDate(p.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Add Prospect modal ───────────────────────────────────────────────────

function AddProspectModal({ onClose }: { onClose: () => void }) {
  const create = useCreateProspect();
  const [form, setForm] = useState<CreateProspectInput>({
    domain: '',
    contactEmail: '',
    contactName: '',
    companyName: '',
    monthlyTrafficTier: 'small',
    source: 'manual'
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync(form);
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to create prospect.';
      setError(msg);
    }
  };

  const field = (
    label: string,
    key: keyof CreateProspectInput,
    placeholder = ''
  ) => (
    <div>
      <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
        {label}
      </label>
      <input
        type="text"
        value={(form[key] as string) || ''}
        onChange={e => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none"
      />
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <form
        className="w-full max-w-lg rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-nurev-text">Add Prospect</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-nurev-muted hover:bg-white/5 hover:text-nurev-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {field('Domain *', 'domain', 'example.com')}
          {field('Contact Email', 'contactEmail', 'john@example.com')}
          {field('Contact Name', 'contactName', 'John Doe')}
          {field('Company Name', 'companyName', 'Acme Inc')}
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Traffic Tier
            </label>
            <Select<TrafficTier>
              value={form.monthlyTrafficTier || 'small'}
              onChange={v => setForm({ ...form, monthlyTrafficTier: v })}
              options={[
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
                { value: 'enterprise', label: 'Enterprise' }
              ]}
              className="w-full"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Source
            </label>
            <Select<ProspectSource>
              value={form.source || 'manual'}
              onChange={v => setForm({ ...form, source: v })}
              options={[
                { value: 'manual', label: 'Manual' },
                { value: 'apollo', label: 'Apollo' },
                { value: 'referral', label: 'Referral' }
              ]}
              className="w-full"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-xs text-red-300">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <ActionButton variant="neutral" size="sm" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            type="submit"
            disabled={!form.domain || create.isPending}
            size="sm"
          >
            {create.isPending ? 'Creating…' : 'Add Prospect'}
          </ActionButton>
        </div>
      </form>
    </div>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────

function DetailModal({
  prospectId,
  onClose
}: {
  prospectId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useProspectDetail(prospectId);
  const pause = usePauseProspect();
  const resume = useResumeProspect();
  const [actionError, setActionError] = useState<string | null>(null);

  const prospect: Prospect | undefined = data?.prospect;
  const onboarding = data?.onboarding || null;
  const outreach = data?.outreach || [];
  const escalations = data?.escalations || [];

  const togglePause = async () => {
    if (!prospect) return;
    setActionError(null);
    try {
      if (prospect.sequencePaused) {
        await resume.mutateAsync({ id: prospectId });
      } else {
        const reason =
          window.prompt('Pause reason? (optional, audit-logged)') || undefined;
        await pause.mutateAsync({ id: prospectId, reason });
      }
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to update sequence pause.';
      setActionError(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex-1">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              <>
                <h3 className="text-lg font-bold text-nurev-text">
                  {prospect?.companyName || prospect?.domain}
                </h3>
                <p className="text-sm text-nurev-muted">{prospect?.domain}</p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isLoading && prospect && (
              <button
                onClick={togglePause}
                disabled={pause.isPending || resume.isPending}
                className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-semibold ring-1 transition disabled:opacity-50 ${
                  prospect.sequencePaused
                    ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/40 hover:bg-emerald-500/20'
                    : 'bg-amber-500/10 text-amber-300 ring-amber-500/40 hover:bg-amber-500/20'
                }`}
                title={
                  prospect.sequencePaused
                    ? `Paused${prospect.sequencePausedReason ? `: ${prospect.sequencePausedReason}` : ''}. Click to resume.`
                    : "Pause this prospect's outreach sequence (does NOT mark dormant)"
                }
              >
                {prospect.sequencePaused ? (
                  <>
                    <Play className="h-3 w-3" />
                    Resume sequence
                  </>
                ) : (
                  <>
                    <Pause className="h-3 w-3" />
                    Pause sequence
                  </>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-nurev-muted hover:bg-white/5 hover:text-nurev-text"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {actionError && (
          <p className="mb-3 text-xs text-red-300">{actionError}</p>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KvCard label="Status">
                <StatusBadge status={prospect?.status} />
              </KvCard>
              <KvCard label="Onboarding">
                <StateBadge state={onboarding?.state} />
              </KvCard>
              <KvCard label="Traffic">
                <span className="text-sm capitalize text-nurev-text">
                  {prospect?.monthlyTrafficTier || '—'}
                </span>
              </KvCard>
              <KvCard label="Score">
                <span className="text-sm text-nurev-text">
                  {prospect?.score ?? 0}
                </span>
              </KvCard>
            </div>

            <Card className="mb-4 bg-nurev-surface">
              <SectionLabel>Contact</SectionLabel>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <KV label="Name">{prospect?.contactName || '—'}</KV>
                <KV label="Email">{prospect?.contactEmail || '—'}</KV>
                <KV label="Source">{prospect?.source || '—'}</KV>
                <KV label="Added">{fmtDate(prospect?.createdAt)}</KV>
              </div>
            </Card>

            {onboarding && (
              <Card className="mb-4 bg-nurev-surface">
                <SectionLabel>Onboarding State</SectionLabel>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <KV label="State">
                    <StateBadge state={onboarding.state} />
                  </KV>
                  <KV label="Entered">{fmtDate(onboarding.stateEnteredAt)}</KV>
                  <KV label="Nudges">{onboarding.nudgeCount ?? 0}</KV>
                  <KV label="Last Nudge">{fmtDate(onboarding.lastNudgeAt)}</KV>
                  {onboarding.notes && (
                    <div className="col-span-2">
                      <KV label="Notes">{onboarding.notes}</KV>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {outreach.length > 0 && (
              <Card className="mb-4 bg-nurev-surface">
                <SectionLabel>Outreach Sequence</SectionLabel>
                <div className="space-y-2">
                  {outreach.map(o => (
                    <div
                      key={o._id}
                      className="flex items-center gap-3 text-sm"
                    >
                      <span className="w-8 font-medium text-amber-200">
                        #{o.sequenceStep}
                      </span>
                      <span className="flex-1 truncate text-nurev-text">
                        {o.emailSubject || '—'}
                      </span>
                      <span className="text-xs text-nurev-muted">
                        {fmtDateTime(o.emailSentAt)}
                      </span>
                      {o.replyReceived && (
                        <span
                          className={`rounded px-2 py-0.5 text-[10px] font-medium ${
                            o.replyIntent === 'interested'
                              ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
                              : o.replyIntent === 'not_interested'
                                ? 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
                                : o.replyIntent === 'question'
                                  ? 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30'
                                  : 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30'
                          }`}
                        >
                          {o.replyIntent}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {escalations.length > 0 && (
              <Card className="bg-nurev-surface">
                <SectionLabel>Escalations</SectionLabel>
                <div className="space-y-2">
                  {escalations.map(e => (
                    <div
                      key={e._id}
                      className="flex items-center gap-3 text-sm"
                    >
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] font-medium ${
                          e.status === 'open'
                            ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30'
                            : e.status === 'resolved'
                              ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30'
                              : 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
                        }`}
                      >
                        {e.status}
                      </span>
                      <span className="flex-1 truncate text-nurev-text">
                        {e.reason}
                      </span>
                      <span className="text-xs text-nurev-muted">
                        {fmtDate(e.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KvCard({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="bg-nurev-surface p-3">
      <p className="text-[10px] uppercase text-nurev-muted">{label}</p>
      <p className="mt-1">{children}</p>
    </Card>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-nurev-muted">{label}:</span>{' '}
      <span className="text-nurev-text">{children}</span>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

function VendorPill({ name, mode }: { name: string; mode?: string }) {
  const live = mode === 'live';
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
      <div className="text-nurev-muted">{name}</div>
      <div
        className={`mt-0.5 font-semibold ${
          live ? 'text-emerald-300' : 'text-amber-300'
        }`}
      >
        {mode || 'unknown'}
      </div>
    </div>
  );
}

function OutreachTab() {
  const { data: config, isLoading } = useAgenticConfig();
  const updateConfig = useUpdateAgenticConfig();
  const runProspecting = useRunProspecting();
  const runSend = useRunSend();

  const [form, setForm] = useState({
    industries: '',
    titles: '',
    companySizeMin: '',
    companySizeMax: '',
    maxResults: ''
  });
  const [seeded, setSeeded] = useState(false);
  const [runOut, setRunOut] = useState('');

  useEffect(() => {
    if (config && !seeded) {
      const src: AgenticIcp =
        config.icpOverrides && Object.keys(config.icpOverrides || {}).length
          ? config.icpOverrides
          : config.icp || {};
      setForm({
        industries: (src.industries || []).join(', '),
        titles: (src.titles || []).join(', '),
        companySizeMin:
          src.companySizeMin != null ? String(src.companySizeMin) : '',
        companySizeMax:
          src.companySizeMax != null ? String(src.companySizeMax) : '',
        maxResults: src.maxResults != null ? String(src.maxResults) : ''
      });
      setSeeded(true);
    }
  }, [config, seeded]);

  if (isLoading || !config) {
    return <Skeleton className="h-64 w-full" />;
  }

  const envLocked = config.envDisabled;
  const running = runProspecting.isPending || runSend.isPending;

  const saveIcp = () => {
    const toList = (s: string) =>
      s
        .split(',')
        .map(x => x.trim())
        .filter(Boolean);
    const toNum = (s: string) => (s.trim() === '' ? undefined : Number(s));
    updateConfig.mutate({
      icp: {
        industries: toList(form.industries),
        titles: toList(form.titles),
        companySizeMin: toNum(form.companySizeMin),
        companySizeMax: toNum(form.companySizeMax),
        maxResults: toNum(form.maxResults)
      }
    });
  };

  const doRun = async (fn: () => Promise<unknown>, label: string) => {
    setRunOut(`Running ${label}…`);
    try {
      const res = await fn();
      setRunOut(`${label}: ${JSON.stringify(res)}`);
    } catch (e) {
      setRunOut(`${label} failed: ${(e as Error)?.message || 'error'}`);
    }
  };

  const text = (key: keyof typeof form, placeholder: string) => (
    <input
      value={form[key]}
      onChange={e => setForm({ ...form, [key]: e.target.value })}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-faint focus:border-amber-500/40 focus:outline-none"
    />
  );

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <SectionLabel>Pipeline status</SectionLabel>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${
                  config.effectiveDisabled ? 'bg-red-400' : 'bg-emerald-400'
                }`}
              />
              <span className="text-lg font-bold text-nurev-text">
                {config.effectiveDisabled ? 'Disabled' : 'Active'}
              </span>
            </div>
            <p className="mt-1 max-w-xl text-xs text-nurev-muted">
              {envLocked
                ? 'Disabled by the AGENTIC_DISABLE env var — override it in the deployment to re-enable.'
                : config.disabled
                ? `All three agents are halted${
                    config.disabledReason ? ` (${config.disabledReason})` : ''
                  }.`
                : 'Prospecting, outreach, and registration agents are running on their crons.'}
            </p>
          </div>
          <ActionButton
            variant={config.disabled ? 'positive' : 'destructive'}
            onClick={() => updateConfig.mutate({ disabled: !config.disabled })}
            disabled={envLocked || updateConfig.isPending}
          >
            {config.disabled ? 'Enable pipeline' : 'Disable pipeline'}
          </ActionButton>
        </div>
      </Card>

      <Card>
        <SectionLabel>Providers</SectionLabel>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <VendorPill
            name="Apollo · prospecting"
            mode={config.vendors?.apollo?.mode}
          />
          <VendorPill
            name="Anthropic · copy"
            mode={config.vendors?.anthropic?.mode}
          />
          <VendorPill
            name={`Email · ${config.vendors?.email?.provider || 'brevo'}`}
            mode={config.vendors?.email?.mode}
          />
        </div>
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <SectionLabel>Claude model · cost lever</SectionLabel>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${
                  config.economyMode || config.envEconomy
                    ? 'bg-amber-400'
                    : 'bg-sky-400'
                }`}
              />
              <span className="text-lg font-bold text-nurev-text">
                {config.economyMode || config.envEconomy
                  ? 'Economy (Haiku)'
                  : 'Standard (Sonnet)'}
              </span>
            </div>
            <p className="mt-1 max-w-xl text-xs text-nurev-muted">
              {config.envEconomy
                ? 'Forced to economy by the AGENTIC_ECONOMY_MODE env var — override it in the deployment to change.'
                : config.economyMode
                ? 'Every pipeline Claude call (cold outreach, reply classification, onboarding) uses Haiku — ~95% cheaper on the output tokens that dominate the bill.'
                : 'Flip to Haiku to cut Claude spend ~95% (cold emails + one-word reply classification are well within Haiku’s range). Switch back any time.'}
            </p>
            <p className="mt-1 font-mono text-[11px] text-nurev-faint">
              active: {config.model?.active || '—'}
            </p>
          </div>
          <ActionButton
            variant={config.economyMode ? 'neutral' : 'info'}
            onClick={() =>
              updateConfig.mutate({ economyMode: !config.economyMode })
            }
            disabled={config.envEconomy || updateConfig.isPending}
          >
            {config.economyMode ? 'Switch to Sonnet' : 'Switch to Haiku'}
          </ActionButton>
        </div>
      </Card>

      <Card>
        <div className="mb-1 flex items-center justify-between">
          <SectionLabel>Apollo targeting (ICP)</SectionLabel>
          <ActionButton
            size="sm"
            onClick={saveIcp}
            disabled={updateConfig.isPending}
          >
            Save targeting
          </ActionButton>
        </div>
        <p className="mb-3 text-xs text-nurev-muted">
          Who the prospecting agent searches for. Comma-separate industries and
          titles. Leave a field blank to use the platform default.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Industries
            </label>
            {text('industries', 'Software, SaaS, Mobile Apps')}
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Contact titles
            </label>
            {text('titles', 'Founder, CEO, CTO, Head of Growth')}
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Company size — min
            </label>
            {text('companySizeMin', '5')}
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Company size — max
            </label>
            {text('companySizeMax', '200')}
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Max results / cycle
            </label>
            {text('maxResults', '50')}
          </div>
        </div>
      </Card>

      <Card>
        <SectionLabel>Run now</SectionLabel>
        <p className="mb-3 text-xs text-nurev-muted">
          Trigger a cycle immediately instead of waiting for the cron. Disabled
          while the pipeline is off.
        </p>
        <div className="flex flex-wrap gap-2">
          <ActionButton
            size="sm"
            variant="neutral"
            disabled={config.effectiveDisabled || running}
            onClick={() =>
              doRun(
                () => runProspecting.mutateAsync(true),
                'Prospecting (dry run)'
              )
            }
          >
            Prospect — dry run
          </ActionButton>
          <ActionButton
            size="sm"
            disabled={config.effectiveDisabled || running}
            onClick={() =>
              doRun(() => runProspecting.mutateAsync(false), 'Prospecting')
            }
          >
            Prospect now
          </ActionButton>
          <ActionButton
            size="sm"
            disabled={config.effectiveDisabled || running}
            onClick={() => doRun(() => runSend.mutateAsync(), 'Send cycle')}
          >
            Send outreach now
          </ActionButton>
        </div>
        {runOut && (
          <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-black/30 p-3 text-[11px] text-nurev-muted">
            {runOut}
          </pre>
        )}
      </Card>
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'prospects', label: 'Prospects' },
  { id: 'outreach', label: 'Outreach' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function PipelineScreen() {
  const [tab, setTab] = useState<Tab>('overview');
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Onboarding Pipeline
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Publisher acquisition funnel. Prospects, onboarding state machine,
            outreach sequences. Long-tail track — the 20-affiliate track lives
            on the dashboard.
          </p>
        </div>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'prospects' && (
        <ProspectsTab
          onSelect={id => setSelected(id)}
          onAdd={() => setShowAdd(true)}
        />
      )}
      {tab === 'outreach' && <OutreachTab />}

      {showAdd && <AddProspectModal onClose={() => setShowAdd(false)} />}
      {selected && (
        <DetailModal prospectId={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
